import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import useIntersectionObserver from '@react-hook/intersection-observer';

import SectionHeading from '@components/Headings/SectionHeading';
import MakeActionButton from '@components/Buttons/MakeActionButton';
import { ConstantLinks } from '@constants/ConstantLinks';
import useAuth from '@hooks/useAuth';
import API from '@lib/API';

function PricingSection({ t }) {
  const AuthAPI = new API();
  const [isAnnualSelected, setisAnnualSelected] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { isUserLoggedIn } = useAuth();

  const [sectionRef, setSectionRef] = useState(null);
  const { isIntersecting } = useIntersectionObserver(sectionRef);
  const hasPlanFetched = useRef(false);

  const [priceConfig, setPriceConfig] = useState({
    FREE: {
      monthly: 0,
      yearly: 0,
    },
    BUSINESS: {
      monthly: '',
      yearly: '',
    },
    UNLIMITED: {
      monthly: '',
      yearly: '',
    },
    isNovExp: false,
  });

  useEffect(() => {
    if (isIntersecting && hasPlanFetched) {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      let planUrl = process.env.GATSBY_PAYMENT_API_URL;

      const headers = {};
      if (isLoggedIn) {
        planUrl += '/v1/pricing_plans';

        headers['authToken'] = localStorage.getItem('authToken');
      } else {
        planUrl += '/v1/public/pricing_plans';
      }
      axios
        .get(planUrl, {
          params: {
            device_id: window.deviceId || '',
          },
          headers,
        })
        .then(response => {
          if (response.data && response.data.plans) {
            const resPlan = response.data.plans;
            const newPlanData = { ...priceConfig };

            newPlanData.BUSINESS = {
              monthly: resPlan[1].durations[0].basePrice,
              yearly: Math.round(resPlan[1].durations[1].basePrice / 12),
            };

            newPlanData.UNLIMITED = {
              monthly: resPlan[2].durations[0].basePrice,
              yearly: Math.round(resPlan[2].durations[1].basePrice / 12),
            };

            setPriceConfig(newPlanData);

            if (response.data['is_corrily_enabled']) {
              AuthAPI.sendAmplitudeEvent('Fetch Corrily Pricing', {
                ExperimentId: response.data['experiment_id'],
                'Corrily Experiment Group': response.data['treatment_group'],
                'Pricing Business Monthly':
                  response.data.plans[1].durations[0].basePrice,
                'Pricing Business Yearly':
                  response.data.plans[1].durations[1].basePrice,
                'Pricing Unlimited Monthly':
                  response.data.plans[2].durations[0].basePrice,
                'Pricing Unlimited Yearly':
                  response.data.plans[2].durations[1].basePrice,
              });
            }
          }
        });
    }
  }, [isIntersecting, hasPlanFetched]);

  const togglePricing = () => {
    setisAnnualSelected(currentPlan => !currentPlan);
  };

  const onGetPaymentPlan = (planName, eventName, btnPos) => {
    sessionStorage.setItem(
      'selectedDuration',
      isAnnualSelected ? 'true' : 'false',
    );
    AuthAPI.sendAmplitudeEvent(eventName, { btnPosition: btnPos });
    window.open('/pricing', '_self');
  };

  return (
    <Pricing ref={setSectionRef}>
      <div className="container mx-auto">
        <SectionHeading className="text-center">
          {t('pricingSection.title')}
        </SectionHeading>

        <PricingSwitch className="pricing-switch my-10 text-center">
          <label
            className="switch-label"
            htmlFor="plan-duration"
            value="monthly"
          >
            {t('pricingSection.monthly')}
          </label>
          <label className="switch">
            <input
              type="checkbox"
              name="duration"
              onChange={togglePricing}
              defaultChecked
              checked={isAnnualSelected}
              id="plan-duration"
            />
            <span className="slider round" />
          </label>
          <label
            className="switch-label"
            htmlFor="plan-duration"
            value="yearly"
          >
            {t('pricingSection.yearly')}{' '}
            <span className="highlight">({t('pricingSection.SAVE50')})</span>
          </label>
        </PricingSwitch>

        <div className="flex justify-center price-cards items-center m-fd-c">
          {!isLoggedIn && (
            <div className="price-block">
              <div className="title">
                <p className="plan">{t('pricingSection.free')}</p>
                <p
                  className="plan-description"
                  dangerouslySetInnerHTML={{
                    __html: t('pricingSection.freeText'),
                  }}
                />
              </div>
              <div className="price">
                <div className="amount">
                  <sup>$</sup>
                  <span id="business-price" className="plan-price">
                    {priceConfig.FREE.monthly}
                  </span>
                  <div className="inline-block">
                    <span className="dur">/{t('pricingSection.month')}</span>
                  </div>
                </div>
              </div>
              <div className="bill-status">
                <p className="status">{t('pricingSection.freeForever')}</p>
                <div className="bill-status-background" />
              </div>
              <div className="action">
                <MakeActionButton
                  onClick={() =>
                    onGetPaymentPlan(
                      'Free',
                      'Click Signup',
                      'Pricing Box - Free',
                    )
                  }
                >
                  {t('pricingSection.knowMore')}
                </MakeActionButton>
              </div>
            </div>
          )}
          <div className="price-block enterprise">
            <div className="title">
              <p className="plan">{t('pricingSection.business')}</p>
              <p
                className="plan-description"
                dangerouslySetInnerHTML={{
                  __html: t('pricingSection.businessText'),
                }}
              />
            </div>
            <div className="price">
              <div className="amount">
                <sup>$</sup>
                <span id="professional-price" className="plan-price">
                  {isAnnualSelected
                    ? priceConfig.BUSINESS.yearly
                    : priceConfig.BUSINESS.monthly}
                </span>
                <div className="inline-block">
                  {isAnnualSelected && (
                    <span className="block text-lg line-through text-gray-500">
                      &nbsp;${priceConfig.BUSINESS.monthly}&nbsp;
                    </span>
                  )}
                  <span className="dur">/{t('pricingSection.month')}</span>
                </div>
              </div>
            </div>
            <div className="bill-status">
              <p className="status">
                {isAnnualSelected
                  ? t('pricingSection.BilledAnnually')
                  : t('pricingSection.BilledMonthly')}
              </p>
              <div className="bill-status-background" />
            </div>
            <div className="action">
              <MakeActionButton
                onClick={() =>
                  onGetPaymentPlan(
                    'Business',
                    'Click Signup',
                    'Pricing Box - Business',
                  )
                }
              >
                {t('pricingSection.knowMore')}
              </MakeActionButton>
            </div>
          </div>

          <div className="price-block">
            <div className="title">
              <p className="plan">{t('pricingSection.unlimited')}</p>
              <p
                className="plan-description"
                dangerouslySetInnerHTML={{
                  __html: t('pricingSection.unlimitedText'),
                }}
              />
            </div>
            <div className="price">
              <div className="amount">
                <sup>$</sup>
                <span id="business-price" className="plan-price">
                  {isAnnualSelected
                    ? priceConfig.UNLIMITED.yearly
                    : priceConfig.UNLIMITED.monthly}
                </span>
                <div className="inline-block">
                  {isAnnualSelected && (
                    <span className="block text-lg line-through text-gray-500">
                      &nbsp;${priceConfig.UNLIMITED.monthly}&nbsp;
                    </span>
                  )}
                  <span className="dur">/{t('pricingSection.month')}</span>
                </div>
              </div>
            </div>
            <div className="bill-status">
              <p className="status">
                {isAnnualSelected
                  ? t('pricingSection.BilledAnnually')
                  : t('pricingSection.BilledMonthly')}
              </p>
              <div className="bill-status-background" />
            </div>
            <div className="action">
              <MakeActionButton
                onClick={() =>
                  onGetPaymentPlan(
                    'Unlimited',
                    'Click Signup',
                    'Pricing Box - Unlimited',
                  )
                }
              >
                {t('pricingSection.knowMore')}
              </MakeActionButton>
            </div>
          </div>
        </div>
        <p className="contact text-center my-10">
          {t('pricingSection.NeedCustomPlan')}&nbsp;
          <a
            href={ConstantLinks.schedule_demo_Link}
            target="_blank"
            rel="noopener noreferrer"
            className="highlight"
          >
            {t('pricingSection.contactUs')}
          </a>
        </p>
      </div>
    </Pricing>
  );
}

export default PricingSection;

const Pricing = styled.section`
  padding: 150px 0;

  .br {
    display: block;
  }

  .highlight {
    color: #2774fc;
  }

  .price-cards {
    .price-block {
      border-radius: 13px;
      box-shadow: 0 13px 59px 0 rgba(136, 148, 162, 0.2);
      background-color: #fff;
      text-align: center;
      padding: 30px;
      margin: 30px 20px;
      position: relative;
      min-width: 300px;
      max-width: 350px;
      width: 342px;

      &::before {
        content: '';
        width: 100%;
        height: 10px;
        background: #2774fc;
        top: 0;
        left: 0;
        position: absolute;
        border-top-right-radius: 10px;
        border-top-left-radius: 10px;
      }

      .title {
        .plan {
          font-size: 24px;
          line-height: normal;
          letter-spacing: 1px;
          color: #2774fc;
          text-transform: uppercase;
        }

        .plan-description {
          font-size: 14px;
          line-height: normal;
          letter-spacing: 1px;
          color: #7a7a7a;
        }
      }

      .price {
        margin: 30px 0 20px;

        sup {
          top: -2em;
          font-size: 0.875rem;
          line-height: normal;
          letter-spacing: 1px;
        }

        .amount {
          .plan-price {
            font-size: 50px;
            line-height: normal;
            letter-spacing: normal;
            color: #000;
            text-transform: uppercase;
            margin: 0;
          }

          .dur {
            font-size: 15px;
            line-height: normal;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
        }
      }

      .bill-status {
        display: inline-block;
        margin-bottom: 10px;
        background-color: #d8e6ff;
        border-radius: 8px;
        padding: 3px 15px;

        .status {
          font-size: 9px;
          line-height: 1.43;
          letter-spacing: 0.5px;
          color: #2774fc;
          margin: 0;
        }
      }
    }

    .action {
      margin: 20px 0;
    }

    .more {
      font-size: 14px;
      line-height: 0.83;
      letter-spacing: 0.5px;
      color: #7a7a7a;
    }

    .enterprise {
      &::before {
        background: #000;
      }
    }
  }

  .contact {
    font-size: 20px;
    line-height: 1.5;
    letter-spacing: 0.5px;
    color: #000;
    text-align: center;
    margin: 30px 0;

    button {
      margin-left: 10px;
    }
  }

  /* SMALL DEVICES (MOBILE) */
  @media (max-width: 767px) {
    padding: 100px 0;
    h2 {
      text-align: center;
    }
  }
`;

const PricingSwitch = styled.div`
  text-align: center;
  margin: 40px 0;
  display: flex;
  align-items: center;
  justify-content: center;

  label {
    margin: 0;
  }

  label.switch-label {
    font-size: 0.75rem;
    line-height: normal;
    letter-spacing: normal;
    padding: 0 15px;
    text-transform: uppercase;
    color: #7a7a7a;
  }

  label.switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 24px;
    vertical-align: middle;
    margin: 0;
  }

  label.switch input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + .slider {
      background-color: #2774fc;
    }

    &:focus + .slider {
      box-shadow: 0 0 1px #2774fc;
    }

    &:checked + .slider:before {
      transform: translateX(26px);
    }
  }

  label.switch .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #2774fc;
    transition: 0.4s;
    border-radius: 34px;

    &:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 2px;
      bottom: 3px;
      background-color: #fff;
      transition: 0.4s;
      border-radius: 50%;
    }
  }
`;
